<template>
  <v-container class="analysis-details-container">
    <Loader v-if="!heading" color="white" text=""></Loader>
    <h2 fluid class="pa-2 analysis-container" v-if="heading">
      {{ heading }}
      <v-icon
        v-if="isGenericCopyToClipboardUnlocked"
        @click="handleCopyClick"
        class="pull-right"
        size="35"
        color="white"
        title="Copy event to Clipboard"
      >
        fas fa-clipboard
      </v-icon>
    </h2>
    <div
      v-if="details.feature"
      class="pa-2 analysis-container analysis-details"
    >
      <p>
        <span class="analysis-title application-heading">{{ title }}</span>
      </p>
      <div v-if="countries">
        <span class="mr-1 analysis-label application-heading">Countries:</span>
        <DetailsListItemClickable
          :content="countries"
          @after-click="handleClose"
          class="analysis-text countries"
        />
      </div>
      <div v-if="risks">
        <span class="mr-1 analysis-label application-heading">Risks:</span>
        <span class="analysis-text">{{ risks }}</span>
      </div>
      <div v-if="published" class="mb-5">
        <span class="mr-1 analysis-label application-heading">Published:</span>
        <span v-html="published" class="analysis-text"></span>
      </div>
      <div class="analysis-body" ref="container">
        <span v-html="body" class="html-body"></span>
        <div v-if="attachments" class="mt-5 analysis-attachments">
          <div v-for="(attachment, index) in attachments" :key="index">
            <a data-type="attachment" :href="attachment.url" target="_blank">
              <v-icon v-if="attachment.type === 'pdf'">far fa-file-pdf</v-icon>
              <v-icon v-else :title="attachment.type">far fa-file</v-icon>
              <span class="ml-2">Attachment</span>
              <span v-if="attachment.sizeFormatted"
                >({{ attachment.sizeFormatted }})</span
              >
            </a>
          </div>
        </div>
      </div>
    </div>
    <v-overlay :absolute="true" :value="screenShotting">
      <div class="copy-overlay-indicator">
        <v-icon size="50" color="white">fas fa-check</v-icon>
        <div><strong>Copied</strong></div>
      </div>
    </v-overlay>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";

import { mapGetters } from "vuex";

import { embedInfoText } from "@/common/basic.utilities";
import { MODULE_PREFIX_ANALYSIS } from "@/store";
import { GET_ANALYSIS_DETAILS } from "@/store/actions.type";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

import { dataToCopy } from "@/services/search/wod.service";

const EMITTED_ACTIONS = {
  CLOSE: "close",
};

export default {
  name: "AnalysisDetails",
  data: () => ({
    screenShotting: false,
    data: null,
  }),
  components: {
    Loader: () => import("@/components/common/Loader"),
    DetailsListItemClickable: () =>
      import("@/components/common/details/DetailsListItemClickable"),
  },
  computed: {
    ...mapGetters({
      details: MODULE_PREFIX_ANALYSIS + "details",
    }),
    heading() {
      if (lodashGet(this.details, "title")) {
        return this.details.title.toUpperCase();
      }
      return null;
    },
    body() {
      if (lodashGet(this.details, "feature.properties.formatted.body")) {
        return this.details.feature.properties.formatted.body;
      }
      return null;
    },
    title() {
      if (lodashGet(this.details, "feature.properties.formatted.title")) {
        return this.details.feature.properties.formatted.title;
      }
      return null;
    },
    risks() {
      if (lodashGet(this.details, "feature.properties.formatted.risks")) {
        return this.details.feature.properties.formatted.risks;
      }
      return null;
    },
    countries() {
      if (
        lodashGet(
          this.details,
          "feature.properties.formatted.countriesWithFlag"
        )
      ) {
        return this.details.feature.properties.formatted.countriesWithFlag;
      }
      return null;
    },
    published() {
      if (lodashGet(this.details, "feature.properties.formatted.published")) {
        return this.details.feature.properties.formatted.published;
      }
      return null;
    },
    attachments() {
      if (lodashGet(this.details, "feature.properties.formatted.attachments")) {
        return this.details.feature.properties.formatted.attachments;
      }
      return null;
    },
    isGenericCopyToClipboardUnlocked() {
      return this.featureUnlocked("generic-copy-to-clipboard");
    },
  },
  methods: {
    handleClickImg(evt) {
      evt.preventDefault();
      const src = evt.currentTarget.getAttribute("src");
      const newTab = window.open();
      newTab.document.body.innerHTML = `<img src="${src}">`;
    },
    async handleCopyClick() {
      document.activeElement.blur();
      const colsToCopy = ["title", "countries", "risks", "published", "body"];
      let item = {};
      item.title = this.title;
      item.countries = this.countries;
      item.risks = this.risks;
      item.published = this.published;
      item.body = this.body;
      this.screenShotting = true;

      await dataToCopy(item, colsToCopy, true);

      setTimeout(() => {
        this.screenShotting = false;
      }, 500);

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.analysis,
        EVENT_ACTIONS.clicked,
        "Copy To Clipboard"
      );
    },
    handleClose() {
      this.$emit(EMITTED_ACTIONS.CLOSE);
    },
  },
  watch: {
    details: {
      handler() {
        lodashDebounce(() => {
          if (lodashGet(this.$refs, "container")) {
            this.$refs.container.querySelectorAll("a").forEach((input) => {
              if (input.getAttribute("data-type") !== "attachment") {
                input.removeAttribute("href");
                const span = document.createElement("span");
                const textNode = document.createTextNode(input.text);
                span.appendChild(textNode);
                input.parentNode.replaceChild(span, input);
              }
            });
            this.$refs.container.querySelectorAll("img").forEach((input) => {
              input.style.width = "300px";
              input.className = "cursor-pointer";
              input.addEventListener("click", this.handleClickImg);
            });

            const htmlBody = this.$refs.container.querySelector(".html-body");
            embedInfoText(
              htmlBody,
              "Show source info",
              `Seerist's analytical updates are produced by a team of over 100 analysts at Control Risks. These analysts are recognized country and subject-matter experts, and are based in the countries or regions that they cover. Our analyst team uses a proprietary risk rating and analytical methodology to assess country risks and ensure global consistency, and all updates undergo a quality review process. Analysts will typically only write an update if an event is being reported by multiple credible sources. However, analysts will consider a variety of factors in their analysis - including historical knowledge, in-country experiences, conversations with contacts on the ground, and experience advising clients - particularly when assessing the likelihood of a potential event or providing an outlook for a risk area or issue.`
            );
          }
        }, 500)();
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.$store.dispatch(MODULE_PREFIX_ANALYSIS + GET_ANALYSIS_DETAILS, {
      title: null,
      feature: null,
    });
    if (lodashGet(this.$refs, "container")) {
      this.$refs.container.querySelectorAll("img").forEach((input) => {
        input.removeEventListener("click", this.handleClickImg);
      });
    }
  },
};
</script>

<style>
.analysis-body ul + p {
  margin-top: 16px;
}
</style>
<style scoped>
.analysis-container {
  background-color: #191a1a !important;
  color: white;
}
.analysis-details {
  display: flex;
  flex-direction: column;
  height: calc(100% - 3em); /* 3em is height of title */
}
.analysis-body {
  font-size: 12px;
  overflow-y: auto;
  padding-right: 15px;
}
.analysis-text {
  font-size: 12px;
}
.analysis-label {
  font-size: 12px;
}
.analysis-text.countries {
  display: inline-block;
}
/* These are for the scroll bar in the content container */
::-webkit-scrollbar {
  width: 8px;
  height: 3px;
}
.dark::-webkit-scrollbar-track {
  background-color: red; /* #646464; */
  border-radius: 3px;
}
.dark::-webkit-scrollbar-track-piece {
  background-color: #000;
}
.dark::-webkit-scrollbar-thumb {
  height: 50px;
  background-color: #666;
  border-radius: 3px;
}
.dark::-webkit-scrollbar-thumb:hover {
  background: black;
}

.v-overlay {
  left: 0px !important;
  background-color: rgba(0, 0, 0, 0) !important;
}

.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.75) !important;
}

.copy-overlay-indicator {
  width: 120px;
  height: 120px;
  background-color: green;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  font-size: large;
}
</style>
